import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import AIQuestionModal from '../../components/AIQuestionModal';

export default function AdminBonusQuestions() {
  const { currentSeason, API } = useContext(AppContext);
  const [questions, setQuestions] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showResponsesDialog, setShowResponsesDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [responses, setResponses] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [showCorrectAnswerDialog, setShowCorrectAnswerDialog] = useState(false);
  const [correctAnswerValue, setCorrectAnswerValue] = useState('');
  const [selectedCorrectResponses, setSelectedCorrectResponses] = useState([]);
  
  const [newQuestion, setNewQuestion] = useState({
    episode_id: '',
    air_date: '',
    question_text: '',
    answer_type: 'yes_no',
    options: [],
    points_value: 5,
    is_published: false,
    allow_answer_edits: false
  });

  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    if (currentSeason) {
      loadData();
    }
  }, [currentSeason]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [questionsRes, episodesRes] = await Promise.all([
        authAxios.get(`${API}/seasons/${currentSeason.id}/bonus-questions`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/episodes`)
      ]);
      setQuestions(questionsRes.data);
      setEpisodes(episodesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createQuestion = async () => {
    if (!newQuestion.episode_id || !newQuestion.question_text) {
      toast.error('Episode and question text are required');
      return;
    }

    try {
      await authAxios.post(
        `${API}/bonus-questions`,
        {
          season_id: currentSeason.id,
          ...newQuestion
        },
        { withCredentials: true }
      );
      toast.success('Question created!');
      setShowCreateDialog(false);
      setNewQuestion({
        episode_id: '',
        air_date: '',
        question_text: '',
        answer_type: 'yes_no',
        options: [],
        points_value: 5,
        is_published: false,
        allow_answer_edits: false
      });
      loadData();
    } catch (error) {
      console.error('Failed to create question:', error);
      toast.error('Failed to create question');
    }
  };

  const openEditDialog = async (question) => {
    setEditingQuestion(question);
    setShowEditDialog(true);
  };

  const updateQuestion = async () => {
    if (!editingQuestion.question_text) {
      toast.error('Question text is required');
      return;
    }

    try {
      await authAxios.patch(
        `${API}/bonus-questions/${editingQuestion.id}`,
        editingQuestion,
        { withCredentials: true }
      );
      toast.success('Question updated!');
      setShowEditDialog(false);
      setEditingQuestion(null);
      loadData();
    } catch (error) {
      console.error('Failed to update question:', error);
      toast.error('Failed to update question');
    }
  };

  const togglePublish = async (questionId, currentStatus) => {
    try {
      await authAxios.patch(
        `${API}/bonus-questions/${questionId}`,
        { is_published: !currentStatus },
        { withCredentials: true }
      );
      toast.success(currentStatus ? 'Unpublished' : 'Published');
      loadData();
    } catch (error) {
      console.error('Failed to toggle publish:', error);
      toast.error('Failed to update');
    }
  };

  const handleDeleteClick = (question) => {
    setQuestionToDelete(question);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!questionToDelete) return;

    try {
      await authAxios.delete(`${API}/bonus-questions/${questionToDelete.id}`);
      toast.success('Bonus question deleted');
      setShowDeleteDialog(false);
      setQuestionToDelete(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete');
      setShowDeleteDialog(false);
    }
  };

  const viewResponses = async (question) => {
    try {
      setSelectedQuestion(question);
      const res = await authAxios.get(`${API}/bonus-questions/${question.id}/responses`);
      setResponses(res.data);
      setShowResponsesDialog(true);
    } catch (error) {
      console.error('Failed to load responses:', error);
      toast.error('Failed to load responses');
    }
  };

  const deleteResponse = async (answerId) => {
    if (!selectedQuestion) return;
    
    if (!window.confirm('Are you sure you want to delete this response? This will also remove any bonus points awarded.')) {
      return;
    }
    
    try {
      await authAxios.delete(`${API}/bonus-questions/${selectedQuestion.id}/responses/${answerId}`);
      toast.success('Response deleted');
      // Refresh responses
      const res = await authAxios.get(`${API}/bonus-questions/${selectedQuestion.id}/responses`);
      setResponses(res.data);
      // Refresh questions to update response count
      loadData();
    } catch (error) {
      console.error('Failed to delete response:', error);
      toast.error('Failed to delete response');
    }
  };

  const setCorrectAnswer = async () => {
    setCorrectAnswerValue('');
    setSelectedCorrectResponses([]);
    setShowCorrectAnswerDialog(true);
  };

  const submitCorrectAnswer = async () => {
    try {
      if (selectedQuestion?.answer_type === 'free_text') {
        // For free text, send list of correct response IDs
        if (selectedCorrectResponses.length === 0) {
          toast.error('Please select at least one correct response');
          return;
        }
        
        await authAxios.post(
          `${API}/bonus-questions/${selectedQuestion.id}/grade-responses`,
          { correct_response_ids: selectedCorrectResponses },
          { withCredentials: true }
        );
      } else {
        // For yes/no and multiple choice, send correct answer value
        if (!correctAnswerValue.trim()) {
          toast.error('Please select a correct answer');
          return;
        }
        
        await authAxios.post(
          `${API}/bonus-questions/${selectedQuestion.id}/set-correct-answer`,
          { answer: correctAnswerValue },
          { withCredentials: true }
        );
      }
      
      toast.success('Responses graded successfully!');
      setShowCorrectAnswerDialog(false);
      viewResponses(selectedQuestion);
    } catch (error) {
      console.error('Failed to set answer:', error);
      toast.error('Failed to grade responses');
    }
  };

  const regradeResponse = async (responseId, newIsCorrect) => {
    try {
      await authAxios.patch(
        `${API}/bonus-questions/${selectedQuestion.id}/responses/${responseId}/regrade`,
        { is_correct: newIsCorrect }
      );
      toast.success(`Response marked as ${newIsCorrect ? 'correct' : 'incorrect'}`);
      // Refresh responses
      viewResponses(selectedQuestion);
    } catch (error) {
      console.error('Failed to regrade:', error);
      toast.error('Failed to regrade response');
    }
  };

  const getStatusBadge = (question) => {
    const now = new Date().toISOString();
    
    if (!question.is_published) {
      return <span className="px-2 py-1 rounded-full text-xs bg-slate-200 text-slate-700">Draft</span>;
    }
    if (now < question.opens_at) {
      return <span className="px-2 py-1 rounded-full text-xs bg-blue-200 text-blue-700">Scheduled</span>;
    }
    if (now >= question.opens_at && now < question.closes_at) {
      return <span className="px-2 py-1 rounded-full text-xs bg-green-200 text-green-700">Open</span>;
    }
    if (question.correct_answer) {
      return <span className="px-2 py-1 rounded-full text-xs bg-purple-200 text-purple-700">Graded</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-orange-200 text-orange-700">Closed</span>;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Denver'
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto container-mobile">
        <div className="spacing-section flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Weekly Bonus Questions
            </h1>
            <p className="text-responsive text-slate-600">Manage bonus questions for episodes</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <Button 
              variant="outline" 
              className="w-full sm:w-auto"
              onClick={() => setShowAIModal(true)}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Question Creation
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto">Create Question</Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Bonus Question</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Episode</Label>
                  <Select 
                    value={newQuestion.episode_id} 
                    onValueChange={(v) => {
                      const selectedEp = episodes.find(ep => ep.id === v);
                      setNewQuestion({
                        ...newQuestion, 
                        episode_id: v,
                        air_date: selectedEp?.air_date ? selectedEp.air_date.split('T')[0] : ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select episode" />
                    </SelectTrigger>
                    <SelectContent>
                      {episodes.map(ep => (
                        <SelectItem key={ep.id} value={ep.id}>
                          Episode {ep.number}
                          {ep.air_date && ` - ${new Date(ep.air_date).toLocaleDateString()}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Air date will be auto-populated from Episode Calendar
                  </p>
                </div>

                <div>
                  <Label>Episode Air Date</Label>
                  <Input
                    type="date"
                    value={newQuestion.air_date}
                    onChange={(e) => setNewQuestion({...newQuestion, air_date: e.target.value})}
                    placeholder="Auto-populated from episode"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Question opens Friday before at 9 AM MT, closes Wednesday at 12 PM MT
                  </p>
                </div>

                <div>
                  <Label>Question Text</Label>
                  <Textarea
                    value={newQuestion.question_text}
                    onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
                    placeholder="Will X win immunity?"
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Answer Type</Label>
                  <Select value={newQuestion.answer_type} onValueChange={(v) => setNewQuestion({...newQuestion, answer_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes_no">Yes/No</SelectItem>
                      <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                      <SelectItem value="free_text">Free Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newQuestion.answer_type === 'multiple_choice' && (
                  <div>
                    <Label>Options (comma-separated)</Label>
                    <Input
                      placeholder="Option A, Option B, Option C"
                      onChange={(e) => setNewQuestion({...newQuestion, options: e.target.value.split(',').map(s => s.trim())})}
                    />
                  </div>
                )}

                <div>
                  <Label>Points Value</Label>
                  <Input
                    type="number"
                    value={newQuestion.points_value}
                    onChange={(e) => setNewQuestion({...newQuestion, points_value: parseInt(e.target.value)})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newQuestion.is_published}
                      onChange={(e) => setNewQuestion({...newQuestion, is_published: e.target.checked})}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <span>Publish immediately</span>
                  </label>
                  
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newQuestion.allow_answer_edits}
                      onChange={(e) => setNewQuestion({...newQuestion, allow_answer_edits: e.target.checked})}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <div>
                      <span>Allow players to edit their answers</span>
                      <p className="text-xs text-slate-500">If unchecked, players cannot change after first submission</p>
                    </div>
                  </label>
                </div>

                <Button onClick={createQuestion} className="w-full">Create Question</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Episode</th>
                      <th>Question</th>
                      <th>Opens</th>
                      <th>Closes</th>
                      <th>Points</th>
                      <th>Responses</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-slate-600">
                          No bonus questions yet. Create one to get started!
                        </td>
                      </tr>
                    ) : (
                      questions.map(q => {
                        const episode = episodes.find(e => e.id === q.episode_id);
                        return (
                          <tr key={q.id}>
                            <td className="font-medium">Ep {episode?.number || '?'}</td>
                            <td className="max-w-md truncate">{q.question_text}</td>
                            <td className="text-sm">{formatDateTime(q.opens_at)}</td>
                            <td className="text-sm">{formatDateTime(q.closes_at)}</td>
                            <td className="text-center">{q.points_value}</td>
                            <td className="text-center">{q.response_count || 0}</td>
                            <td>{getStatusBadge(q)}</td>
                            <td>
                              <div className="flex gap-1 items-center justify-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditDialog(q)}
                                  className="h-8 w-8 p-0"
                                  title="Edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewResponses(q)}
                                  className="h-8 w-8 p-0"
                                  title="View Responses"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant={q.is_published ? "default" : "outline"}
                                  onClick={() => togglePublish(q.id, q.is_published)}
                                  className="h-8 w-8 p-0"
                                  title={q.is_published ? "Unpublish" : "Publish"}
                                >
                                  {q.is_published ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                    </svg>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteClick(q)}
                                  className="h-8 w-8 p-0"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        {editingQuestion && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Bonus Question</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Question Text</Label>
                  <Textarea
                    value={editingQuestion.question_text}
                    onChange={(e) => setEditingQuestion({...editingQuestion, question_text: e.target.value})}
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Points Value</Label>
                  <Input
                    type="number"
                    value={editingQuestion.points_value}
                    onChange={(e) => setEditingQuestion({...editingQuestion, points_value: parseInt(e.target.value)})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingQuestion.allow_answer_edits}
                      onChange={(e) => setEditingQuestion({...editingQuestion, allow_answer_edits: e.target.checked})}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <div>
                      <span>Allow players to edit their answers</span>
                      <p className="text-xs text-slate-500">If unchecked, players cannot change after first submission</p>
                    </div>
                  </label>
                </div>

                <Button onClick={updateQuestion} className="w-full">Update Question</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Responses Dialog */}
        <Dialog open={showResponsesDialog} onOpenChange={setShowResponsesDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Responses - {selectedQuestion?.question_text}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="flex gap-2">
                {!selectedQuestion?.correct_answer && (
                  <Button onClick={setCorrectAnswer}>Set Correct Answer & Grade</Button>
                )}
                {selectedQuestion?.correct_answer && (
                  <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-md">
                    <span className="text-sm text-green-700">
                      Correct Answer: <strong>{selectedQuestion.correct_answer}</strong>
                    </span>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto max-h-96">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Answer</th>
                      <th>Submitted</th>
                      <th>Correct</th>
                      <th>Points</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-600">No responses yet</td>
                      </tr>
                    ) : (
                      responses.map(r => (
                        <tr key={r.id}>
                          <td>{r.user?.name}</td>
                          <td>{r.answer_value}</td>
                          <td className="text-sm">{formatDateTime(r.submitted_at)}</td>
                          <td>
                            {r.is_correct === true && (
                              <button 
                                onClick={() => regradeResponse(r.id, false)}
                                className="text-green-600 hover:bg-green-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                title="Click to mark as incorrect"
                              >
                                ✓ Correct
                              </button>
                            )}
                            {r.is_correct === false && (
                              <button 
                                onClick={() => regradeResponse(r.id, true)}
                                className="text-red-600 hover:bg-red-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                title="Click to mark as correct"
                              >
                                ✗ Incorrect
                              </button>
                            )}
                            {r.is_correct === null && (
                              <div className="flex gap-1">
                                <button 
                                  onClick={() => regradeResponse(r.id, true)}
                                  className="text-slate-400 hover:text-green-600 hover:bg-green-50 px-2 py-1 rounded transition-colors text-sm"
                                  title="Mark as correct"
                                >
                                  ✓
                                </button>
                                <button 
                                  onClick={() => regradeResponse(r.id, false)}
                                  className="text-slate-400 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded transition-colors text-sm"
                                  title="Mark as incorrect"
                                >
                                  ✗
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="font-bold">{r.points_awarded}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteResponse(r.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Set Correct Answer Dialog */}
      <Dialog open={showCorrectAnswerDialog} onOpenChange={setShowCorrectAnswerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Correct Answer & Grade Responses</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-600 mb-4">
                <strong>Question:</strong> {selectedQuestion?.question_text}
              </p>
              
              <Label>Correct Answer</Label>
              
              {/* Yes/No Questions */}
              {selectedQuestion?.answer_type === 'yes_no' && (
                <Select value={correctAnswerValue} onValueChange={setCorrectAnswerValue}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select correct answer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {/* Multiple Choice Questions */}
              {selectedQuestion?.answer_type === 'multiple_choice' && (
                <Select value={correctAnswerValue} onValueChange={setCorrectAnswerValue}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select correct answer" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedQuestion?.options?.map((option, index) => (
                      <SelectItem key={index} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Free Text Questions */}
              {selectedQuestion?.answer_type === 'free_text' && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Select which player responses should be considered correct:
                  </p>
                  <div className="border rounded-md p-3 max-h-64 overflow-y-auto space-y-2">
                    {responses.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No player responses yet
                      </p>
                    ) : (
                      <>
                        <div className="flex items-center space-x-2 p-2 border-b">
                          <Checkbox
                            id="select-all-responses"
                            checked={selectedCorrectResponses.length === responses.length}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCorrectResponses(responses.map(r => r.id));
                              } else {
                                setSelectedCorrectResponses([]);
                              }
                            }}
                          />
                          <label htmlFor="select-all-responses" className="font-medium cursor-pointer text-sm">
                            Select All ({responses.length} responses)
                          </label>
                        </div>
                        {responses.map((response) => (
                          <div key={response.id} className="flex items-start space-x-2 p-2 hover:bg-gray-50 rounded">
                            <Checkbox
                              id={`response-${response.id}`}
                              checked={selectedCorrectResponses.includes(response.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCorrectResponses(prev => [...prev, response.id]);
                                } else {
                                  setSelectedCorrectResponses(prev => prev.filter(id => id !== response.id));
                                }
                              }}
                            />
                            <label 
                              htmlFor={`response-${response.id}`} 
                              className="cursor-pointer flex-1 text-sm"
                            >
                              <div>
                                <div className="font-medium">{response.user_name}</div>
                                <div className="text-gray-600 mt-1 bg-gray-50 p-2 rounded text-xs">
                                  "{response.answer_value}"
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  {responses.length > 0 && (
                    <p className="text-sm text-gray-600">
                      {selectedCorrectResponses.length} response{selectedCorrectResponses.length !== 1 ? 's' : ''} selected as correct
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="text-sm text-slate-600 bg-blue-50 p-3 rounded">
              <p className="font-medium mb-1">What happens next:</p>
              <ul className="space-y-1 text-xs">
                <li>• Player responses will be automatically graded</li>
                <li>• Points will be awarded to players with correct answers</li>
                <li>• Leaderboard will be updated with new scores</li>
              </ul>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCorrectAnswerDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={submitCorrectAnswer} 
                disabled={
                  selectedQuestion?.answer_type === 'free_text' 
                    ? selectedCorrectResponses.length === 0
                    : !correctAnswerValue.trim()
                }
              >
                {selectedQuestion?.answer_type === 'free_text' ? 'Grade Selected Responses' : 'Set Answer & Grade'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Bonus Question</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Are you sure you want to delete this bonus question?
            </p>
            <p className="text-sm text-slate-600">
              <strong>Question:</strong> {questionToDelete?.question_text}
            </p>
            <p className="text-sm text-red-600 font-medium">
              Warning: This will also delete all player responses to this question. This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete Question & Responses
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AIQuestionModal
        open={showAIModal}
        onClose={() => setShowAIModal(false)}
        episodes={episodes}
        currentSeason={currentSeason}
        API={API}
        onQuestionApproved={loadData}
      />
    </Layout>
  );
}
